class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('bb-cart-popup') || document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    if (this.notification) {
      this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    }

    // Add close button listeners after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupCloseButtons());
    } else {
      this.setupCloseButtons();
    }
  }

  setupCloseButtons() {
    const closeButtons = document.querySelectorAll('.bb-cart-popup__close');
    closeButtons.forEach((closeButton) => {
      closeButton.addEventListener('click', () => this.close());
    });
  }

  getVisibleProperties(properties) {
    if (!properties) return [];

    const entries = Array.isArray(properties) ? properties : Object.entries(properties);

    return entries.filter(([key, value]) => {
      if (!key || !value) return false;

      const normalizedKey = key.toString().toLowerCase();
      return !normalizedKey.startsWith('_') && normalizedKey !== 'design' && !normalizedKey.includes('mcz');
    });
  }

  escapeHTML(value) {
    return value
      .toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  open() {
    if (!this.notification) return;

    // Use new popup structure if available
    if (this.notification.id === 'bb-cart-popup') {
      this.notification.classList.add('active');
      this.notification.focus();
    } else {
      // Fallback to original structure
      this.notification.classList.add('animate', 'active');
      this.notification.addEventListener(
        'transitionend',
        () => {
          this.notification.focus();
          if (typeof trapFocus !== 'undefined') trapFocus(this.notification);
        },
        { once: true }
      );
    }

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (!this.notification) return;

    this.notification.classList.remove('active', 'animate');
    document.body.removeEventListener('click', this.onBodyClick);

    if (typeof removeTrapFocus !== 'undefined' && this.activeElement) {
      removeTrapFocus(this.activeElement);
    }
  }

  async renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    // Use new popup if available
    if (document.getElementById('bb-cart-popup')) {
      await this.renderNewPopup(parsedState);
    } else {
      // Fallback to original
      this.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          element.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      });
    }

    if (this.header) this.header.reveal();
    this.open();
  }

  async renderNewPopup(parsedState) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const itemContainer = document.getElementById('bb-cart-popup-item');
      const itemContainerMobile = document.getElementById('bb-cart-popup-item-mobile');
      const summaryContainer = document.getElementById('bb-cart-popup-summary');

      if ((itemContainer || itemContainerMobile) && cart.items.length > 0) {
        // Find the item by key if provided, otherwise use the last item
        let item = parsedState.key
          ? cart.items.find((i) => i.key === parsedState.key)
          : cart.items[cart.items.length - 1];

        if (!item) item = cart.items[cart.items.length - 1];
        const image = item.image
          ? `<img src="${item.image}" alt="${item.product_title}" class="bb-cart-popup__item-image">`
          : '';

        // Build variant title and properties
        let variantTitle = '';
        if (item.variant_title && item.variant_title !== 'Default Title') {
          variantTitle = `<div class="bb-cart-popup__item-option">${item.variant_title.toUpperCase()}</div>`;
        }

        // Add customer-facing properties only. Kickflip internals are stored as private line item properties.
        this.getVisibleProperties(item.properties).forEach(([key, value]) => {
          variantTitle += `<div class="bb-cart-popup__item-option">${this.escapeHTML(key)}: ${this.escapeHTML(value)}</div>`;
        });

        const price = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
          item.final_price / 100
        );

        const self = this;
        const itemHTML = `
          <div class="bb-cart-popup__item-box">
            ${image}
            <div class="bb-cart-popup__item-details">
              <h3 class="bb-cart-popup__item-name">${item.product_title.toUpperCase()}</h3>
              <p class="bb-cart-popup__item-price">${price}</p>
              ${variantTitle}
              <div class="bb-cart-popup__item-controls">
                <div class="bb-cart-popup__quantity-controls">
                  <button class="bb-cart-popup__quantity-btn" data-action="decrease" data-key="${item.key}">-</button>
                  <span class="bb-cart-popup__quantity-value">${item.quantity}</span>
                  <button class="bb-cart-popup__quantity-btn" data-action="increase" data-key="${item.key}">+</button>
                </div>
                <button class="bb-cart-popup__remove" data-action="remove" data-key="${item.key}">Remove</button>
              </div>
            </div>
          </div>
        `;

        // Render to desktop container
        if (itemContainer) {
          itemContainer.innerHTML = itemHTML;
        }

        // Render to mobile container
        if (itemContainerMobile) {
          itemContainerMobile.innerHTML = itemHTML;
        }

        // Add event listeners to quantity buttons (both desktop and mobile)
        const containers = [itemContainer, itemContainerMobile].filter(Boolean);
        containers.forEach((container) => {
          container.querySelectorAll('[data-action]').forEach((btn) => {
            btn.addEventListener('click', function (e) {
              e.preventDefault();
              const key = this.dataset.key;
              const action = this.dataset.action;
              const currentQty = parseInt(container.querySelector('.bb-cart-popup__quantity-value').textContent);
              if (action === 'decrease') {
                self.updateQuantity(key, currentQty - 1);
              } else if (action === 'increase') {
                self.updateQuantity(key, currentQty + 1);
              } else if (action === 'remove') {
                self.removeItem(key);
              }
            });
          });
        });
      }

      if (summaryContainer && cart.items.length > 0) {
        // Show the same item in summary as in the item section
        const summaryItem = parsedState.key
          ? cart.items.find((i) => i.key === parsedState.key)
          : cart.items[cart.items.length - 1];

        if (!summaryItem) return;

        const price = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
          summaryItem.final_line_price / 100
        );
        const variantTitle =
          summaryItem.variant_title && summaryItem.variant_title !== 'Default Title'
            ? `<div class="bb-cart-popup__summary-option">${summaryItem.variant_title.toUpperCase()}</div>`
            : '';

        // Get variant title and properties for summary
        let summaryVariantTitle = '';
        if (summaryItem.variant_title && summaryItem.variant_title !== 'Default Title') {
          summaryVariantTitle = `<div class="bb-cart-popup__summary-option">${summaryItem.variant_title.toUpperCase()}</div>`;
        }

        // Add customer-facing properties only. Kickflip internals are stored as private line item properties.
        let propertiesHtml = '';
        this.getVisibleProperties(summaryItem.properties).forEach(([key, value]) => {
          propertiesHtml += `<div class="bb-cart-popup__summary-option">${this.escapeHTML(key)}: ${this.escapeHTML(value)}</div>`;
        });

        summaryContainer.innerHTML = `
          <div class="bb-cart-popup__summary-item">
            <img src="${summaryItem.image}" alt="${summaryItem.product_title}" class="bb-cart-popup__summary-image">
            <div class="bb-cart-popup__summary-details">
              <h4 class="bb-cart-popup__summary-name">${summaryItem.product_title.toUpperCase()}</h4>
              ${summaryVariantTitle}
              ${propertiesHtml}
            </div>
            <p class="bb-cart-popup__summary-price">${price}</p>
          </div>
        `;

        // Update total
        const totalElement = document.querySelector('.bb-cart-popup__summary-total-amount');
        if (totalElement) {
          const total = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
            cart.total_price / 100
          );
          totalElement.textContent = total;
        }

        // Update item count label
        const itemCountElement = document.querySelector('.bb-cart-popup__summary-label');
        if (itemCountElement) {
          itemCountElement.textContent = cart.item_count === 1 ? '1 ITEM' : `${cart.item_count} ITEMS`;
        }
      }

      // Load suggestions from same collection
      if (cart.items.length > 0) {
        const addedItem = parsedState.key
          ? cart.items.find((i) => i.key === parsedState.key)
          : cart.items[cart.items.length - 1];

        // if (addedItem && addedItem.url) {
        //   await this.loadSuggestions(addedItem.url, addedItem.product_id);
        // }

        // after
        if (addedItem) {
          await this.loadSuggestions(addedItem.product_id);
        }
      }

      // Setup promotion code and note handlers
      this.setupPromoAndNote();

      // Setup checkout handler
      this.setupCheckoutHandler();

      // Load existing note from cart attributes
      this.loadExistingNote(cart);
    } catch (error) {
      console.error('Error rendering cart popup:', error);
    }
  }

  setupPromoAndNote() {
    // Toggle promo input visibility
    const promoLink = document.getElementById('bb-cart-popup-promo-link');
    const promoContainer = document.getElementById('bb-cart-popup-promo-input-container');

    if (promoLink && promoContainer) {
      promoLink.addEventListener('click', (e) => {
        e.preventDefault();
        const isVisible = promoContainer.style.display !== 'none';
        promoContainer.style.display = isVisible ? 'none' : 'flex';
        if (!isVisible) {
          const input = document.getElementById('bb-cart-popup-promo-input-desktop');
          if (input) input.focus();
        }
      });
    }

    // Toggle note input visibility
    const noteLink = document.getElementById('bb-cart-popup-note-link');
    const noteContainer = document.getElementById('bb-cart-popup-note-input-container');

    if (noteLink && noteContainer) {
      noteLink.addEventListener('click', (e) => {
        e.preventDefault();
        const isVisible = noteContainer.style.display !== 'none';
        noteContainer.style.display = isVisible ? 'none' : 'block';
        if (!isVisible) {
          const input = document.getElementById('bb-cart-popup-note-input-desktop');
          if (input) input.focus();
        }
      });
    }

    // Promotion code handlers - handle both desktop and mobile
    const promoInputs = [
      document.getElementById('bb-cart-popup-promo-input'),
      document.getElementById('bb-cart-popup-promo-input-desktop'),
    ].filter(Boolean);

    const promoButtons = [
      document.getElementById('bb-cart-popup-promo-button'),
      document.getElementById('bb-cart-popup-promo-button-desktop'),
    ].filter(Boolean);

    const promoMessages = [
      document.getElementById('bb-cart-popup-promo-message'),
      document.getElementById('bb-cart-popup-promo-message-desktop'),
    ].filter(Boolean);

    promoButtons.forEach((promoButton, index) => {
      const promoInput = promoInputs[index];
      if (promoButton && promoInput) {
        promoButton.addEventListener('click', () => {
          const code = promoInput.value.trim();
          // Matching checkout logic: just store it, discount will be applied at checkout
          if (code) {
            console.log('Promo code entered:', code);
            // The input has name="discount" so it will be submitted with checkout
            promoInput.setAttribute('data-applied-code', code.toUpperCase().trim());
          }
        });

        // Allow Enter key to apply
        promoInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const code = promoInput.value.trim();
            if (code) {
              console.log('Promo code entered:', code);
              promoInput.setAttribute('data-applied-code', code.toUpperCase().trim());
            }
          }
        });
      }
    });

    // Note handlers - handle both desktop and mobile (matching checkout logic)
    const noteTextarea = document.getElementById('bb-cart-popup-note-input-desktop');
    
    if (noteTextarea && !noteTextarea.hasAttribute('data-auto-save-initialized')) {
      noteTextarea.setAttribute('data-auto-save-initialized', 'true');
      
      // Debounce function (matching checkout)
      const debounce = (func, wait) => {
        let timeout;
        return function executedFunction(...args) {
          const later = () => {
            clearTimeout(timeout);
            func(...args);
          };
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
        };
      };

      // Auto-save on change with 300ms debounce (matching checkout)
      const debouncedSaveNote = debounce((event) => {
        const note = event.target.value;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ note: note })
        })
        .then(response => response.json())
        .then(data => {
          console.log('Note auto-saved successfully');
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:updated', {
              bubbles: true,
              detail: { cart: data }
            })
          );
        })
        .catch(error => {
          console.error('Error auto-saving note:', error);
        });
      }, 300);
      
      noteTextarea.addEventListener('change', debouncedSaveNote);
    }
  }

  async applyPromoCode(code, messageElement = null) {
    // Matching checkout logic: promo code is stored with name="discount" and submitted at checkout
    // No need to validate or apply here - just store it
    if (!code) {
      return;
    }

    const promoInputs = [
      document.getElementById('bb-cart-popup-promo-input'),
      document.getElementById('bb-cart-popup-promo-input-desktop'),
    ].filter(Boolean);

    promoInputs.forEach((input) => {
      if (input) {
        input.setAttribute('data-applied-code', code.toUpperCase().trim());
      }
    });

    console.log('Promo code entered:', code);
    // The discount will be calculated and applied at checkout via name="discount"
  }

  updateCheckoutButton(discountCode) {
    // Update checkout form to include discount code
    const checkoutForm = document.getElementById('bb-cart-popup-checkout-form');
    if (checkoutForm && discountCode) {
      // Update form action to include discount code
      const currentAction = checkoutForm.getAttribute('action');
      const checkoutUrl = currentAction.includes('/checkout') ? currentAction : '/checkout';
      checkoutForm.setAttribute('action', checkoutUrl + '?discount=' + encodeURIComponent(discountCode));
    }
  }

  setupCheckoutHandler() {
    // Intercept checkout form submission to include discount code
    const checkoutForm = document.getElementById('bb-cart-popup-checkout-form');
    const checkoutButton = document.getElementById('bb-cart-popup-checkout-button');
    const checkoutFormMobile = document.getElementById('bb-cart-popup-checkout-form-mobile');
    const checkoutButtonMobile = document.getElementById('bb-cart-popup-checkout-button-mobile');

    const handleCheckout = (e) => {
      // Try to get discount code from any promo input
      const promoInputs = [
        document.getElementById('bb-cart-popup-promo-input'),
        document.getElementById('bb-cart-popup-promo-input-desktop'),
      ].filter(Boolean);

      let discountCode = null;
      for (const promoInput of promoInputs) {
        discountCode = promoInput?.getAttribute('data-applied-code') || promoInput?.value.trim();
        if (discountCode) break;
      }

      if (discountCode) {
        // Redirect to checkout with discount code
        e.preventDefault();
        window.location.href = '/checkout?discount=' + encodeURIComponent(discountCode.toUpperCase());
      }
    };

    if (checkoutButton) {
      checkoutButton.addEventListener('click', handleCheckout);
    }

    if (checkoutButtonMobile) {
      checkoutButtonMobile.addEventListener('click', handleCheckout);
    }
  }

  async saveNote(note) {
    try {
      // Save note to cart attributes (Shopify uses 'note' for order notes)
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: note || '',
          attributes: {
            'Order Note': note || '',
          },
        }),
      });

      if (response.ok) {
        // Note saved successfully - it will be included in the order
        console.log('Note saved to cart');
      } else {
        console.error('Error saving note');
      }
    } catch (error) {
      console.error('Error saving note:', error);
    }
  }

  loadExistingNote(cart) {
    const noteInputs = [
      document.getElementById('bb-cart-popup-note-input'),
      document.getElementById('bb-cart-popup-note-input-desktop'),
    ].filter(Boolean);

    noteInputs.forEach((noteInput) => {
      if (noteInput) {
        // Load from cart note (Shopify standard) or attributes
        if (cart.note) {
          noteInput.value = cart.note;
        } else if (cart.attributes && cart.attributes['Order Note']) {
          noteInput.value = cart.attributes['Order Note'];
        }
      }
    });
    
    // Also handle textarea (for desktop)
    const noteTextarea = document.getElementById('bb-cart-popup-note-input-desktop');
    if (noteTextarea && cart.note) {
      noteTextarea.value = cart.note;
    }

    // Also load existing discount code if any
    const promoInputs = [
      document.getElementById('bb-cart-popup-promo-input'),
      document.getElementById('bb-cart-popup-promo-input-desktop'),
    ].filter(Boolean);

    promoInputs.forEach((promoInput) => {
      if (promoInput && cart.attributes && cart.attributes['discount_code']) {
        promoInput.value = cart.attributes['discount_code'];
        promoInput.setAttribute('data-applied-code', cart.attributes['discount_code']);
      }
    });
  }

  showPromoMessage(message, type, messageElement = null) {
    const promoMessages = messageElement
      ? [messageElement]
      : [
          document.getElementById('bb-cart-popup-promo-message'),
          document.getElementById('bb-cart-popup-promo-message-desktop'),
        ].filter(Boolean);

    promoMessages.forEach((promoMessage) => {
      if (promoMessage) {
        promoMessage.textContent = message;
        promoMessage.className = 'bb-cart-popup__promo-message ' + type;
        promoMessage.style.display = 'block';

        // Hide message after 5 seconds
        setTimeout(() => {
          promoMessage.style.display = 'none';
        }, 5000);
      }
    });
  }

  updateCartTotals(cart) {
    // Update total amount
    const totalElement = document.querySelector('.bb-cart-popup__summary-total-amount');
    if (totalElement) {
      const total = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        cart.total_price / 100
      );
      totalElement.textContent = total;
    }

    // Update item count
    const itemCountElement = document.querySelector('.bb-cart-popup__summary-label');
    if (itemCountElement) {
      itemCountElement.textContent = cart.item_count === 1 ? '1 ITEM' : `${cart.item_count} ITEMS`;
    }

    // Update order summary total
    const orderTotalElement = document.querySelector(
      '.bb-cart-popup__order-row.bb-cart-popup__order-total span:last-child'
    );
    if (orderTotalElement) {
      const total = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        cart.total_price / 100
      );
      orderTotalElement.textContent = total;
    }
  }

async loadSuggestions(productId) {
  if (!productId) {
    this.clearSuggestions();
    return;
  }

  try {
    const response = await fetch(
      `/recommendations/products.json?product_id=${productId}&limit=4&intent=related`
    );

    if (!response.ok) {
      this.clearSuggestions();
      return;
    }

    const data = await response.json();
    const products = (data.products || []).filter(p => p.id !== 8477749739698).slice(0, 3);

    this.renderSuggestions(products);
  } catch (error) {
    console.error('Error loading suggestions:', error);
    this.clearSuggestions();
  }
}

renderSuggestions(products) {
  const desktopGrid = document.getElementById('bb-cart-popup-suggestions');
  const mobileGrid = document.getElementById('bb-cart-popup-suggestions-mobile');

  if (!products.length) {
    this.clearSuggestions();
    return;
  }

  const desktopHTML = products.map((product) => this.buildSuggestionCard(product, 'desktop')).join('');
  const mobileHTML = products.map((product) => this.buildSuggestionCard(product, 'mobile')).join('');

  if (desktopGrid) desktopGrid.innerHTML = desktopHTML;
  if (mobileGrid) mobileGrid.innerHTML = mobileHTML;
}

buildSuggestionCard(product, variant = 'desktop') {
  const prefix = variant === 'mobile' ? 'bb-cart-popup__mobile-suggestion' : 'bb-cart-popup__suggestion';
  const image = product.featured_image?.url || product.featured_image || '';
  const title = this.escapeHTML(product.title || '');
  const url = product.url || `/products/${product.handle}`;
  const price = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((product.price || 0) / 100);

  return `
    <a href="${url}" class="${prefix}-item">
      ${image ? `<img src="${image}" alt="${title}" class="${prefix}-image" loading="lazy">` : ''}
      <div class="${prefix}-content">
        <h4 class="${prefix}-name">${title}</h4>
        <p class="${prefix}-price">${price}</p>
      </div>
    </a>
  `;
}

clearSuggestions() {
  const desktopGrid = document.getElementById('bb-cart-popup-suggestions');
  const mobileGrid = document.getElementById('bb-cart-popup-suggestions-mobile');
  if (desktopGrid) desktopGrid.innerHTML = '';
  if (mobileGrid) mobileGrid.innerHTML = '';
}

  async updateQuantity(key, quantity) {
    if (quantity < 1) {
      this.removeItem(key);
      return;
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      });
      const cart = await response.json();
      await this.renderNewPopup({ key: key });
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }

  async removeItem(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      this.close();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      window.location.reload();
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    const popup = document.getElementById('bb-cart-popup');
    const isNewPopup = popup && (target === popup || target.closest('#bb-cart-popup'));
    const isOldPopup = target !== this.notification && !target.closest('cart-notification');

    if (isNewPopup && target === popup) {
      this.close();
    } else if (isOldPopup) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);

// Make instance available globally for onclick handlers
window.cartNotificationInstance = document.querySelector('cart-notification');

// Global function for saving note from onclick handler (matching checkout)
function saveCartPopupNote() {
  const noteTextarea = document.getElementById('bb-cart-popup-note-input-desktop');
  const note = noteTextarea ? noteTextarea.value.trim() : '';

  fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      note: note
    })
  })
  .then(response => response.json())
  .then(data => {
    console.log('Note saved successfully');
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: data }
      })
    );
  })
  .catch(error => {
    console.error('Error saving note:', error);
  });
}
